import axios from 'axios';
import { getVersionInfo } from '../utils/version';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface BackendVersionInfo {
  status: string;
  environment: string;
  version: {
    git_hash: string;
    git_branch: string;
    build_date: string;
    deploy_date: string;
  };
  services: {
    database: string;
    cognito: string;
    lambda: string;
  };
}

export interface HealthCheckResult {
  frontend: {
    gitHash: string;
    gitBranch: string;
    buildDate: string;
    environment: string;
  };
  backend: BackendVersionInfo | null;
  error?: string;
}

export async function checkBackendHealth(): Promise<HealthCheckResult> {
  const frontendInfo = getVersionInfo();
  
  try {
    const response = await axios.get<BackendVersionInfo>(`${API_BASE_URL}/health/version`, {
      timeout: 5000, // 5 second timeout
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    return {
      frontend: {
        gitHash: frontendInfo.gitHash,
        gitBranch: frontendInfo.gitBranch,
        buildDate: frontendInfo.buildDate,
        environment: frontendInfo.environment,
      },
      backend: response.data,
    };
  } catch (error) {
    console.warn('Failed to check backend health:', error);
    
    // Log more detailed error information
    if (axios.isAxiosError(error)) {
      console.warn('Axios error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        }
      });
    }
    
    return {
      frontend: {
        gitHash: frontendInfo.gitHash,
        gitBranch: frontendInfo.gitBranch,
        buildDate: frontendInfo.buildDate,
        environment: frontendInfo.environment,
      },
      backend: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function logVersionInfo(healthResult: HealthCheckResult): void {
  const { frontend, backend, error } = healthResult;
  
  console.group('🚀 Simple Handshake - Version Information');
  
  console.log('📱 Frontend:', {
    gitHash: frontend.gitHash,
    gitBranch: frontend.gitBranch,
    buildDate: new Date(frontend.buildDate).toLocaleString(),
    environment: frontend.environment,
  });
  
  if (backend) {
    console.log('🔧 Backend:', {
      status: backend.status,
      environment: backend.environment,
      gitHash: backend.version.git_hash,
      gitBranch: backend.version.git_branch,
      buildDate: new Date(backend.version.build_date).toLocaleString(),
      deployDate: new Date(backend.version.deploy_date).toLocaleString(),
      services: backend.services,
    });
  } else {
    console.warn('⚠️ Backend health check failed:', error);
  }
  
  console.groupEnd();
} 